import { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import styles from './SbRespFollowTopButton.scss';
import { trackFollowSearchButton } from '../../utils/tracking/searchBrowse/trackFollowSearch';
import { locations } from '../../utils/tracking/shared/followSearchTracking';
import { getQueryParam } from '../../utils/uriUtils';

// Components
import { FollowButton } from '../../components/global/FollowButton/FollowButton';
import { TooltipLoader } from '../../global/TooltipLoader';
import { FormattedMessage } from 'dibs-react-intl';

class SbRespFollowTopButtonComponent extends Component {
    constructor() {
        super();

        this.handleFollowSearchCompleted = this.handleFollowSearchCompleted.bind(this);
        this.shouldAutoFollow = this.shouldAutoFollow.bind(this);

        this.state = {
            showSuccessTooltip: false,
            loadSuccessTooltip: false,
            showHoverTooltip: false,
            loadHoverTooltip: false,
            // Putting this in componentDidMount caused the FollowButton to update before we had user information.
            // We need user information when FollowButton updates to determine appropriate action
            shouldAutoFollow: this.shouldAutoFollow(),
        };
    }

    handleFollowSearchCompleted(isFollowing) {
        const { refetchFollow } = this.props;
        refetchFollow(() => {
            if (isFollowing) {
                // show success tooltip is user follows a search (but not if they unfollow)
                this.setState(
                    {
                        loadSuccessTooltip: true,
                        showSuccessTooltip: true,
                        showHoverTooltip: false,
                    },
                    () => {
                        // close success tooltip after 3 seconds
                        const timer = setTimeout(() => {
                            this.setState({ showSuccessTooltip: false });
                            clearTimeout(timer);
                        }, 3000);
                    }
                );
            }
        });

        trackFollowSearchButton(isFollowing);
    }

    shouldAutoFollow() {
        if (typeof document !== 'undefined') {
            return getQueryParam(document.location.search, 'follow') === 'search';
        }
        return false;
    }

    render() {
        const {
            user,
            itemSearch,
            showFollowButton,
            followSearchValue,
            isFollowing,
            isCategorySearch,
        } = this.props;
        const {
            showHoverTooltip,
            loadHoverTooltip,
            loadSuccessTooltip,
            showSuccessTooltip,
            shouldAutoFollow,
        } = this.state;

        // only show hover tooltip if user is not following search
        const mouseProps = {
            onMouseEnter: () =>
                this.setState({ showHoverTooltip: !isFollowing, loadHoverTooltip: !isFollowing }),
            onMouseLeave: () => this.setState({ showHoverTooltip: false }),
        };

        if (!showFollowButton) {
            return null;
        }

        const followSearchCopy = isCategorySearch ? followSearchValue : `"${followSearchValue}"`;

        return (
            <div {...mouseProps} className={styles.container}>
                <FollowButton
                    size="medium"
                    userId={get(user, 'serviceId') || null}
                    user={user}
                    page={itemSearch.displayUriRef}
                    name={followSearchValue}
                    autoFollow={shouldAutoFollow}
                    favoritesConnection={get(this.props, 'user.favorites') || null}
                    location={locations.SEARCH_BROWSE}
                    onFollowSearchCompleted={this.handleFollowSearchCompleted}
                />
                <TooltipLoader
                    load={loadHoverTooltip}
                    isVisible={showHoverTooltip}
                    closeOnOutsideClick
                    direction="bottom"
                >
                    <FormattedMessage
                        id="sb.SbRespFollowTopButton.hoverText"
                        defaultMessage="Get weekly updates with new arrivals for this category."
                    />
                </TooltipLoader>
                <TooltipLoader
                    load={loadSuccessTooltip}
                    isVisible={showSuccessTooltip}
                    closeOnOutsideClick
                    direction="right"
                >
                    <FormattedMessage
                        id="sb.SbRespFollowTopButton.successText"
                        defaultMessage="Check your email for weekly updates in {name}."
                        values={{
                            name: (
                                <span data-tn="follow-search-value" className={styles.searchTerm}>
                                    {followSearchCopy}
                                </span>
                            ),
                        }}
                    />
                </TooltipLoader>
            </div>
        );
    }
}

SbRespFollowTopButtonComponent.defaultProps = {
    onFollowSearchSuccess: () => {},
};

SbRespFollowTopButtonComponent.propTypes = {
    user: PropTypes.object,
    itemSearch: PropTypes.object,
    showFollowButton: PropTypes.bool.isRequired,
    followSearchValue: PropTypes.string.isRequired,
    isFollowing: PropTypes.bool.isRequired,
    onFollowSearchSuccess: PropTypes.func,
    refetchFollow: PropTypes.func,
    isCategorySearch: PropTypes.bool.isRequired,
};

export const SbRespFollowTopButton = createFragmentContainer(SbRespFollowTopButtonComponent, {
    itemSearch: graphql`
        fragment SbRespFollowTopButton_itemSearch on ItemSearchQueryConnection {
            displayUriRef
        }
    `,
    user: graphql`
        fragment SbRespFollowTopButton_user on User {
            serviceId
            favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages) {
                ...FollowButton_favoritesConnection
            }
        }
    `,
});
